import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, inject } from "@angular/core";
import { provideTranslocoScope, TranslocoService } from "@jsverse/transloco";
import { CerrixPromptComponent } from "@app/shared/cerrix-prompt/cerrix-prompt.component";
import {
    GenericListField,
    GenericListFieldType,
} from "@app/shared/models/GenericList/GenericListField";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { TabEventListenerType } from "@enums/TabEventListenerType.enum";
import { TabMenu } from "@models/generic/TabModels/TabMenu";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { TabService } from "@services/tabs/TabService";
import { of, firstValueFrom } from "rxjs";
import { DashboardService } from "../services/dashboard.service";
import { DashboardModel } from "../shared/models/DashboardModel";

@Component({
    selector: "dashboard-manager",
    templateUrl: "./dashboard-manager.component.html",
    styleUrls: ["./dashboard-manager.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope("dashboard")]
})
export class DashboardManagerComponent implements OnInit {
    private _tabService = inject(TabService);
    private _ds = inject(DashboardService);
    private _prompt = inject(CerrixPromptService);
    private _transloco = inject(TranslocoService);

    @ViewChild("allDashboardView", { static: true }) allDashboardView: ElementRef;

    @Input() cerrixTab: TabModel;
    @Output() load = new EventEmitter<DashboardModel>();

    currentDashboard: DashboardModel;

    // Guid of the dashboard that owns the clean root URL (the default / initial pick). Loading
    // it clears config.activemenu; every other dashboard sets activemenu to its guid.
    private defaultDashboardGuid: string;

    openPrompt: CerrixPromptComponent;
    favorites: DashboardModel[] = [];
    roleGroups: DashboardModel[] = [];

    allDashboards: DashboardModel[];

    scopeFilter: string;
    scopedDashboards: DashboardModel[];

    async ngOnInit() {
        // This component's own imperative translate() calls (toasts, prompts, tab menu item
        // names) run before dashboard.component's toolbar pipes can render -- those are gated
        // behind a loaded dashboard, which this component is what loads. Preload the scope so
        // those calls don't return raw keys (load() needs the "scope/lang" form; a bare scope
        // name is read as a language).
        await firstValueFrom(this._transloco.load(`dashboard/${this._transloco.getActiveLang()}`));

        if (this.cerrixTab.identifier === this._tabService.activeTab.identifier) {
            this.initDashboard();
        } else {
            const focusListenerID = this._tabService.listeners.addTabListener(
                TabEventListenerType.OnFocus,
                this.cerrixTab.identifier,
                () => {
                    if (this.cerrixTab.menu) {
                        return;
                    }

                    this.initDashboard();
                    this._tabService.listeners.removeListener(focusListenerID);
                },
                this.cerrixTab
            );
        }
    }

    initDashboard() {
        this.createPersonalView(true);
    }

    async loadDashboard(dashboard: DashboardModel) {
        if (this.currentDashboard && this.currentDashboard._unsavedChanges) {
            const continueLoad = await this._prompt
                .confirm(
                    this._transloco.translate("dashboard.dashboardManager.unsavedChangesTitle"),
                    this._transloco.translate("dashboard.dashboardManager.unsavedChangesMessage")
                )
                .toPromise();

            if (!continueLoad) {
                return;
            }
        }

        if (!dashboard.api) {
            this.bindApi(dashboard);
        }

        if (this.cerrixTab.menu) {
            this.cerrixTab.menu.activeMenuItem = dashboard.guid;
        }

        this.currentDashboard = dashboard;
        this.load.emit(dashboard);

        // Reflect the active dashboard in the browser URL: the default dashboard keeps the
        // clean root URL, every other dashboard is addressable as /dashboard?activemenu=<guid>.
        if (!this.cerrixTab.config) {
            this.cerrixTab.config = {};
        }
        this.cerrixTab.config.activemenu =
            dashboard.guid && dashboard.guid === this.defaultDashboardGuid ? null : dashboard.guid;
        this._tabService.activeTabMenuChangedEvent.emit(this.cerrixTab.identifier);

        if (this.openPrompt) {
            this.openPrompt.close();
            this.openPrompt = null;
        }
    }

    /**
     * Loads the initial/default dashboard (favorite marked default -> first favorite ->
     * first role-group -> system default) and records its guid as the clean-root owner.
     */
    private async loadDefaultDashboard() {
        const pick =
            this.favorites.find((x) => x.default) ?? this.favorites[0] ?? this.roleGroups[0];

        if (pick) {
            this.defaultDashboardGuid = pick.guid;
            this.loadDashboard(pick);
            return;
        }

        const x = await this.getDefaultDashboard(false);
        this.defaultDashboardGuid = x.guid;
        this.loadDashboard(x);
    }

    /**
     * Loads a dashboard by guid for a deeplink / Back-Forward. Resolves from the sidebar first,
     * then falls back to fetching all dashboards so dashboards opened via "Browse all" (not in
     * the sidebar) still restore. Unknown guids fall back to the default dashboard.
     */
    async selectDashboardByGuid(guid: string) {
        let dashboard =
            this.favorites.find((d) => d.guid === guid) ??
            this.roleGroups.find((d) => d.guid === guid) ??
            (this.allDashboards ?? []).find((d) => d.guid === guid);

        if (!dashboard) {
            await this.loadBrowseAllData();
            dashboard = (this.allDashboards ?? []).find((d) => d.guid === guid);
        }

        if (dashboard) {
            this.loadDashboard(dashboard);
        } else {
            this.loadDefaultDashboard();
        }
    }

    createPersonalView(loadInitial: boolean) {
        this.favorites = [];
        this.roleGroups = [];

        this._ds.getPersonal().subscribe((data) => {
            data.forEach((db) => {
                this.bindApi(db);
            });

            this.favorites = data.filter((d) => d.favorite);
            this.roleGroups = data.filter((d) => !d.favorite && d.roleGroupAssigned);

            if (loadInitial) {
                // The default/initial pick owns the clean root URL; capture it before honoring
                // a deeplink so switching back to it later restores the clean URL.
                this.defaultDashboardGuid = (
                    this.favorites.find((x) => x.default) ??
                    this.favorites[0] ??
                    this.roleGroups[0]
                )?.guid;

                const requested = this.cerrixTab.config?.activemenu;
                if (requested) {
                    // No sidebar item owns the default: resolve the async system default too, so
                    // a later Back-to-root recognises the clean-URL owner and clears activemenu
                    // instead of leaving it set.
                    if (!this.defaultDashboardGuid) {
                        this.getDefaultDashboard(false).then((x) => {
                            this.defaultDashboardGuid = x.guid;
                        });
                    }
                    this.selectDashboardByGuid(requested);
                } else {
                    this.loadDefaultDashboard();
                }
            }

            this.createDefaultSideMenu();
        });
    }

    showBrowseAll() {
        if (this.openPrompt) {
            this.openPrompt.close();
            this.openPrompt = null;
        }

        this.loadBrowseAllData();

        this.openPrompt = this._prompt.prompt({
            maxWidth: "1200px",
            maxHeight: "800px",
            data: {
                title: this._transloco.translate("dashboard.dashboardManager.browseAllTitle"),
                customTemplate: this.allDashboardView,
                confirmButton: {
                    show: false,
                },
                cancelButton: {
                    text: this._transloco.translate("dashboard.dashboardManager.closeButton"),
                },
            },
        });
    }

    private async loadBrowseAllData() {
        this.allDashboards = null;

        let data = await firstValueFrom(this._ds.getAll());
        if (!data) {
            data = [];
        }

        data.forEach((db) => {
            this.bindApi(db);
        });

        this.allDashboards = data;
        this.filterDashboards();
    }

    filterDashboards() {
        if (!this.scopeFilter) {
            this.scopedDashboards = this.allDashboards;
            return;
        }

        const keywords = this.scopeFilter.toLowerCase().split(" ");
        const compare = (db: DashboardModel, text: string) => {
            if (
                db.name.toLowerCase().indexOf(text) >= 0 ||
                db.description.toLowerCase().indexOf(text) >= 0 ||
                db.createdBy.toLowerCase().indexOf(text) >= 0
            ) {
                return true;
            }

            return false;
        };

        this.scopedDashboards = [];
        this.allDashboards.forEach((db) => {
            if (keywords.some((kw) => compare(db, kw))) {
                this.scopedDashboards.push(db);
            }
        });
    }

    bindApi(dashboard: DashboardModel) {
        if (!dashboard.api) {
            dashboard.api = {};
        }

        dashboard.api.save = this.saveDashboard.bind(this, dashboard);
        dashboard.api.delete = this.delete.bind(this, dashboard);
        dashboard.api.showInfo = this.showDashboardInfo.bind(this, dashboard);
        dashboard.api.toggleFavorite = this.toggleFavorite.bind(this, dashboard);
        dashboard.api.setDefault = this.setAsDefault.bind(this, dashboard);
    }

    async saveDashboard(dashboard: DashboardModel, skipSaveCheck?: boolean) {
        if (!dashboard.canEdit) {
            this._prompt.alert({
                data: {
                    title: this._transloco.translate("dashboard.dashboardManager.cannotSaveTitle"),
                    message: this._transloco.translate(
                        "dashboard.dashboardManager.cannotSaveMessage"
                    ),
                },
            });
            return false;
        }

        const save = skipSaveCheck
            ? true
            : await this._prompt
                  .confirm(
                      this._transloco.translate("dashboard.dashboardManager.saveChangesTitle"),
                      this._transloco.translate("dashboard.dashboardManager.saveChangesMessage")
                  )
                  .toPromise();

        if (save) {
            try {
                const result = await firstValueFrom(this._ds.store(dashboard));

                Object.keys(result).forEach(function (key) {
                    dashboard[key] = result[key];
                });

                dashboard._unsavedChanges = false;
                this.currentDashboard = result;
                this.createPersonalView(false);
                return true;
            } catch {
                return false;
            }
        } else {
            dashboard._unsavedChanges = true;
            return true;
        }
    }

    async toggleFavorite(dashboard: DashboardModel) {
        if (!dashboard.guid) {
            this._prompt.alert({
                data: {
                    title: this._transloco.translate("dashboard.dashboardManager.notSavedTitle"),
                    message: this._transloco.translate(
                        "dashboard.dashboardManager.notSavedToggleFavoriteMessage"
                    ),
                },
            });

            return;
        }

        if (dashboard.default) {
            this._prompt.alert({
                data: {
                    title: this._transloco.translate(
                        "dashboard.dashboardManager.cannotUnfavoriteTitle"
                    ),
                    message: this._transloco.translate(
                        "dashboard.dashboardManager.cannotUnfavoriteMessage"
                    ),
                },
            });

            return;
        }

        if (dashboard.favorite) {
            await firstValueFrom(this._ds.unfavorite(dashboard.guid));
            dashboard.favorite = false;
            dashboard.default = false;
        } else {
            await firstValueFrom(this._ds.favorite(dashboard.guid));
            dashboard.favorite = true;
        }

        this.createPersonalView(false);
    }

    async setAsDefault(dashboard: DashboardModel) {
        if (!dashboard.guid) {
            this._prompt.alert({
                data: {
                    title: this._transloco.translate("dashboard.dashboardManager.notSavedTitle"),
                    message: this._transloco.translate(
                        "dashboard.dashboardManager.notSavedSetDefaultMessage"
                    ),
                },
            });

            return;
        }

        if (dashboard.default) {
            await firstValueFrom(this._ds.resetDefault(dashboard.guid));
            dashboard.default = false;
            dashboard.favorite = true;
        } else {
            await firstValueFrom(this._ds.default(dashboard.guid));
            dashboard.default = true;
            dashboard.favorite = true;
        }

        this.loadBrowseAllData();
        this.createPersonalView(false);
    }

    async delete(dashboard: DashboardModel) {
        if (!dashboard.guid) {
            this._prompt.alert({
                data: {
                    title: this._transloco.translate("dashboard.dashboardManager.notSavedDeleteTitle"),
                    message: this._transloco.translate(
                        "dashboard.dashboardManager.notSavedDeleteMessage"
                    ),
                },
            });

            return;
        }

        if (dashboard.default) {
            const ignoreDefault = await this._prompt
                .confirm(
                    this._transloco.translate("dashboard.dashboardManager.defaultDashboardConfirmTitle"),
                    this._transloco.translate(
                        "dashboard.dashboardManager.defaultDashboardConfirmMessage"
                    )
                )
                .toPromise();

            if (!ignoreDefault) {
                return false;
            }
        }

        await firstValueFrom(this._ds.deleteDashboard(dashboard.guid));

        this.loadBrowseAllData();
        this.createPersonalView(false);
    }

    async createNew() {
        if (!this.allDashboards && !Array.isArray(this.allDashboards)) {
            await this.loadBrowseAllData();
        }

        const myDefaultDashboard = Array.isArray(this.allDashboards)
            ? this.allDashboards.find((d) => d.default)
            : null;
        const systemDefaultIdentifier = "##systemDefaultDashboard";
        const emptyIdentifier = "##emptyDashboard";

        this._prompt
            .prompt({
                maxWidth: "500px",
                maxHeight: "500px",
                data: {
                    title: this._transloco.translate("dashboard.dashboardManager.newDashboardTitle"),
                    message: this._transloco.translate(
                        "dashboard.dashboardManager.newDashboardMessage"
                    ),
                    fields: [
                        {
                            fieldName: "template",
                            prettyName: this._transloco.translate(
                                "dashboard.dashboardManager.templateFieldLabel"
                            ),
                            fieldType: GenericListFieldType.SingleSelect,
                            required: true,
                            selfInit: true,
                            getDataMethod: () => {
                                const options: { ID: string; Name: string }[] = [];

                                options.push({
                                    ID: emptyIdentifier,
                                    Name: this._transloco.translate(
                                        "dashboard.dashboardManager.emptyOptionLabel"
                                    ),
                                });

                                if (myDefaultDashboard) {
                                    options.push({
                                        ID: myDefaultDashboard.guid,
                                        Name: this._transloco.translate(
                                            "dashboard.dashboardManager.myDefaultOptionLabel",
                                            { name: myDefaultDashboard.name }
                                        ),
                                    });
                                }

                                options.push({
                                    ID: systemDefaultIdentifier,
                                    Name: this._transloco.translate(
                                        "dashboard.dashboardManager.systemDefaultOptionLabel"
                                    ),
                                });

                                if (this.allDashboards.length > 0) {
                                    const dbTemplates = this.allDashboards
                                        .map((db) => {
                                            return { ID: db.guid, Name: db.name };
                                        })
                                        .filter(
                                            (db) =>
                                                !myDefaultDashboard ||
                                                myDefaultDashboard.guid != db.ID
                                        );

                                    options.push(...dbTemplates);
                                }

                                return of(options);
                            },
                        },
                    ],
                    fieldResult: {
                        template: emptyIdentifier,
                    },
                    confirmButton: {
                        text: this._transloco.translate("dashboard.dashboardManager.createButton"),
                        icon: "far fa-plus",
                    },
                },
            })
            .getResult()
            .subscribe(async (result) => {
                const selectedTemplate = result ? result["template"] : null;
                if (selectedTemplate) {
                    let toLoad: DashboardModel;

                    if (myDefaultDashboard && selectedTemplate == myDefaultDashboard.guid) {
                        toLoad = this.createCopyOfDashboard(myDefaultDashboard);
                    } else if (selectedTemplate == systemDefaultIdentifier) {
                        toLoad = await this.getDefaultDashboard(true);
                    } else if (selectedTemplate == emptyIdentifier) {
                        toLoad = this.getEmptyDashboard();
                    } else {
                        const existing = this.allDashboards.find((d) => d.guid == selectedTemplate);
                        toLoad = this.createCopyOfDashboard(existing);
                    }

                    if (toLoad) {
                        this.loadDashboard(toLoad);
                    }
                }
            });
    }

    private getEmptyDashboard() {
        const emptyDashboard: DashboardModel = {
            guid: null,

            name: "",
            description: "",
            icon: "fad fa-th",
            screenSize: window.screen.width + "x" + window.screen.height,

            createdBy: "-",
            updatedOn: new Date(),

            isOwner: true,
            canEdit: true,

            systemDefault: false,
            public: false,

            default: false,
            favorite: true,
            roleGroupAssigned: false,
            roleGroups: [],

            widgetConfigurations: "",
            globalFilterValues: {},

            _editOnLoad: true,
        };

        return emptyDashboard;
    }

    private async getDefaultDashboard(isNewDashboard: boolean) {
        let dashboard = await firstValueFrom(this._ds.getDefault());

        if (isNewDashboard || !dashboard.guid) {
            const template = this.getEmptyDashboard();

            template.name = dashboard.name;
            template.description = dashboard.description;
            template.widgetConfigurations = dashboard.widgetConfigurations;
            template.globalFilterValues = dashboard.globalFilterValues;

            if (!isNewDashboard) {
                template.isOwner = false;
                template.favorite = false;
                template._editOnLoad = false;
            }

            dashboard = template;
        } else {
            dashboard._editOnLoad = false;
        }

        return dashboard;
    }

    private createCopyOfDashboard(model: DashboardModel) {
        const newModel = this.getEmptyDashboard();

        newModel.name = model.name;
        newModel.description = model.description;
        newModel.icon = model.icon;

        newModel.widgetConfigurations = model.widgetConfigurations;
        newModel.globalFilterValues = model.globalFilterValues;

        return newModel;
    }

    showDashboardInfo(dashboard: DashboardModel) {
        const currentResolution = window.screen.width + "x" + window.screen.height;
        const fields: GenericListField[] = [];
        let height = 530;

        if (dashboard.description) {
            height += 175;
            fields.push({
                prettyName: this._transloco.translate("dashboard.dashboardManager.descriptionFieldLabel"),
                fieldName: "description",
                fieldType: GenericListFieldType.TextArea,
                isReadonly: true,
            });
        }

        fields.addRange([
            {
                prettyName: this._transloco.translate("dashboard.dashboardManager.createdByFieldLabel"),
                fieldName: "createdBy",
                fieldType: GenericListFieldType.Text,
                isReadonly: true,
            },
            {
                prettyName: this._transloco.translate(
                    "dashboard.dashboardManager.lastUpdatedOnFieldLabel"
                ),
                fieldName: "updatedOn",
                fieldType: GenericListFieldType.DateTime,
                isReadonly: true,
            },
            {
                prettyName: this._transloco.translate(
                    "dashboard.dashboardManager.optimizedResolutionFieldLabel"
                ),
                fieldName: "screenSize",
                description:
                    currentResolution !== dashboard.screenSize
                        ? this._transloco.translate(
                              "dashboard.dashboardManager.resolutionMismatchDescription",
                              { resolution: currentResolution }
                          )
                        : undefined,
                fieldType: GenericListFieldType.Text,
                isReadonly: true,
            },
            {
                prettyName: this._transloco.translate("dashboard.dashboardManager.isDefaultFieldLabel"),
                fieldName: "default",
                fieldType: GenericListFieldType.CheckBox,
                isReadonly: true,
                hideFillers: true,
            },
            {
                prettyName: this._transloco.translate("dashboard.dashboardManager.isFavoritedFieldLabel"),
                fieldName: "favorite",
                fieldType: GenericListFieldType.CheckBox,
                isReadonly: true,
                hideFillers: true,
            },
            {
                prettyName: this._transloco.translate(
                    "dashboard.dashboardManager.isSystemDefaultFieldLabel"
                ),
                fieldName: "systemDefault",
                fieldType: GenericListFieldType.CheckBox,
                isReadonly: true,
                hideFillers: true,
            },
            {
                prettyName: this._transloco.translate(
                    "dashboard.dashboardManager.inheritedFromRoleGroupFieldLabel"
                ),
                fieldName: "roleGroupAssigned",
                fieldType: GenericListFieldType.CheckBox,
                isReadonly: true,
                hideFillers: true,
            },
        ]);

        this._prompt.prompt({
            maxWidth: "700px",
            maxHeight: height + "px",
            data: {
                title: dashboard.name,
                confirmButton: {
                    show: false,
                },
                cancelButton: {
                    text: this._transloco.translate("dashboard.dashboardManager.closeButton"),
                },
                fields: fields,
                fieldResult: dashboard,
            },
        });
    }

    private createDefaultSideMenu() {
        const menu = new TabMenu();
        menu.menuItemClicked = (menuItem) => {
            if (menuItem.menuItemId === "createnew") {
                this.createNew();
            } else if (menuItem.menuItemId === "browse") {
                this.showBrowseAll();
            } else {
                let foundItem = this.favorites.find((i) => i.guid == menuItem.menuItemId);
                if (!foundItem) {
                    foundItem = this.roleGroups.find((i) => i.guid == menuItem.menuItemId);
                }

                if (foundItem) {
                    this.loadDashboard(foundItem);
                    menu.activeMenuItem = menuItem.identifier;
                }
            }
        };

        // Back/Forward hooks: base root URL re-selects the default dashboard; an activemenu
        // guid resolves any dashboard (including non-sidebar ones) via selectDashboardByGuid.
        menu.selectDefaultMenuItem = () => this.loadDefaultDashboard();
        menu.selectMenuItemById = (guid) => this.selectDashboardByGuid(guid);

        if (this.currentDashboard) {
            menu.activeMenuItem = this.currentDashboard.guid;
        }

        menu.menuItems = [];

        const createNewItem = new TabMenuItem();
        createNewItem.name = this._transloco.translate("dashboard.dashboardManager.createNewMenuItem");
        createNewItem.iconClass = "fal fa-plus";
        createNewItem.menuItemId = "createnew";
        menu.menuItems.push(createNewItem);

        const browseItem = new TabMenuItem();
        browseItem.name = this._transloco.translate("dashboard.dashboardManager.browseAllMenuItem");
        browseItem.iconClass = "fad fa-folder-open";
        browseItem.menuItemId = "browse";
        menu.menuItems.push(browseItem);

        if (this.favorites.any()) {
            const favorites = new TabMenuItem();
            favorites.name = this._transloco.translate("dashboard.dashboardManager.favoritesMenuItem");
            favorites.iconClass = "fas fa-star";
            favorites.menuItemId = "favorites";
            favorites.clickable = false;
            favorites.children = this.favorites.map((i) => {
                const item = new TabMenuItem();
                item.identifier = i.guid;
                item.name = i.name;
                item.iconClass = i.icon;
                item.menuItemId = i.guid;
                return item;
            });
            menu.menuItems.push(favorites);
        }

        if (this.roleGroups.any()) {
            const rolegroups = new TabMenuItem();
            rolegroups.name = this._transloco.translate("dashboard.dashboardManager.roleGroupMenuItem");
            rolegroups.iconClass = "fad fa-folder-open";
            rolegroups.menuItemId = "rolegroups";
            rolegroups.clickable = false;
            rolegroups.children = this.roleGroups.map((i) => {
                const item = new TabMenuItem();
                item.identifier = i.guid;
                item.name = i.name;
                item.iconClass = i.icon;
                item.menuItemId = i.guid;
                return item;
            });
            menu.menuItems.push(rolegroups);
        }

        if (!this.cerrixTab.menu) {
            this.cerrixTab.menu = menu;
        } else {
            this.cerrixTab.menu = null;
            // This forces the front to render correctly, when have updates for the menu.
            setTimeout(() => {
                this.cerrixTab.menu = menu;
            }, 10);
        }
    }
}
